package org.vreo.devicehub.features.userAuthentication.presentation.register

import org.koin.compose.viewmodel.koinViewModel
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.userAuthentication.presentation.register.components.RequirementCheckText
import org.vreo.devicehub.features.userAuthentication.presentation.components.PasswordOutlinedTextField

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.login_field_placeholder_password
import devicehub.composeapp.generated.resources.register_button_title
import devicehub.composeapp.generated.resources.register_field_password_check_length
import devicehub.composeapp.generated.resources.register_field_password_check_lowercase
import devicehub.composeapp.generated.resources.register_field_password_check_number
import devicehub.composeapp.generated.resources.register_field_password_check_uppercase
import devicehub.composeapp.generated.resources.register_field_placeholder_company_address_city
import devicehub.composeapp.generated.resources.register_field_placeholder_company_address_country
import devicehub.composeapp.generated.resources.register_field_placeholder_company_address_postal_code
import devicehub.composeapp.generated.resources.register_field_placeholder_company_address_street
import devicehub.composeapp.generated.resources.register_field_placeholder_company_name
import devicehub.composeapp.generated.resources.register_field_placeholder_confirm_password
import devicehub.composeapp.generated.resources.register_field_placeholder_email
import devicehub.composeapp.generated.resources.register_field_placeholder_first_name
import devicehub.composeapp.generated.resources.register_field_placeholder_last_name
import devicehub.composeapp.generated.resources.register_field_placeholder_mobile_number
import devicehub.composeapp.generated.resources.register_view_company_address_title
import devicehub.composeapp.generated.resources.register_view_title
import devicehub.composeapp.generated.resources.register_view_user_details_title


/**
 * User registration screen root
 *
 * @param viewModel view model
 * @param onUserRegistrationSuccess success handler, action taken on successful registration and
 * verification of the new user
 * @param onBackNavigationClick back navigation handler
 */
@Composable
fun UserRegistrationScreenRoot(
    viewModel: UserRegistrationViewModel = koinViewModel(),
    onUserRegistrationSuccess: (userEmail: String, validUntil: Long) -> Unit,
    onBackNavigationClick: () -> Unit,
) {
    val state: UserRegistrationState by viewModel.state.collectAsStateWithLifecycle()

    UserRegistrationView(
        state = state,
        onAction = { action ->
            val actionRequest = when (action) {
                is UserRegistrationActions.OnBackNavigationClicked -> {
                    onBackNavigationClick()
                    action
                }

                is UserRegistrationActions.OnRegisterClicked -> {
                    action.copy(callback = onUserRegistrationSuccess)
                }

                else -> action
            }

            viewModel.onAction(actionRequest)
        }
    )
}

/**
 * User registration view, provides access to new user registration ui
 *
 * @param state composable state, manages state of all fields
 * @param onAction action handle
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UserRegistrationView(
    state: UserRegistrationState,
    onAction: (UserRegistrationActions) -> Unit,
) {
    val scrollState = rememberScrollState()
    val focusManager = LocalFocusManager.current

    Scaffold(
        topBar = {
            CenterAlignedTopAppBar(
                title = {},
                navigationIcon = {
                    IconButton(
                        onClick = {
                            onAction(UserRegistrationActions.OnBackNavigationClicked())
                        },
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = null,
                        )
                    }
                },
            )
        },
    ) { innerPadding ->
        Box(
            contentAlignment = Alignment.TopCenter,
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            Column(
                modifier = Modifier
                    .padding(16.dp)
                    .verticalScroll(scrollState)
            ) {
                Text(
                    text = stringResource(Res.string.register_view_title),
                    style = MaterialTheme.typography.displaySmall.copy(
                        fontWeight = FontWeight.Bold,
                    ),
                )

                HorizontalDivider(
                    modifier = Modifier
                        .padding(bottom = 8.dp)
                )

                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier
                        .widthIn(max = 900.dp)
                ) {
                    RegistrationForm(
                        state = state,
                        focusManager = focusManager,
                        onAction = onAction,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp)
                    )

                    Button(
                        onClick = {
                            onAction(UserRegistrationActions.OnRegisterClicked())
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(24.dp)
                    ) {
                        Text(stringResource(Res.string.register_button_title))
                    }

                    Spacer(
                        modifier = Modifier.padding(32.dp)
                    )
                }
            }
        }

        // Conditional section for displaying loading and error states
        if (state.isLoading) {
            LoadingDialog()
        } else if (state.error != null) {
            ErrorDialog(
                dialogTitle = stringResource(Res.string.error_general_title),
                dialogText = state.error.asString(),
                onDismissRequest = {
                    onAction(UserRegistrationActions.OnClearError())
                },
                onConfirmation = {
                    onAction(UserRegistrationActions.OnClearError())
                }
            )
        }
    }
}

/**
 * Registration form composable, includes all required fields for user registration
 *
 * @param state composable state, manages state of all fields
 * @param onAction action handle
 * @param focusManager focus manager
 * @param modifier modifier
 */
@Composable
private fun RegistrationForm(
    state: UserRegistrationState,
    onAction: (UserRegistrationActions) -> Unit,
    focusManager: FocusManager,
    modifier: Modifier = Modifier
) {
    Column(
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(16.dp),
        modifier = modifier
    ) {
        // User details
        Text(
            stringResource(Res.string.register_view_user_details_title),
            style = MaterialTheme.typography.titleMedium
        )

        HorizontalDivider(
            modifier = Modifier
                .fillMaxWidth()
        )

        UserDetailsSection(
            state = state,
            onAction = onAction,
            focusManager = focusManager,
        )

        // Password
        Column(
            verticalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 16.dp, bottom = 16.dp)
        ) {
            PasswordOutlinedTextField(
                value = state.password,
                placeholder = {
                    Text(stringResource(Res.string.login_field_placeholder_password))
                },
                onValueChange = {
                    onAction(UserRegistrationActions.OnUpdatePassword(it))
                },
                isError = state.invalidPassword,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Password,
                    imeAction = ImeAction.Next
                ),
                keyboardActions = KeyboardActions(
                    onNext = { focusManager.moveFocus(FocusDirection.Down) }
                ),
                colors = transparentTextFieldColors(),
                modifier = Modifier
                    .fillMaxWidth()
            )

            RequirementCheckText(
                text = stringResource(Res.string.register_field_password_check_length),
                valid = state.passwordMinimumLengthCheck
            )

            RequirementCheckText(
                text = stringResource(Res.string.register_field_password_check_uppercase),
                valid = state.passwordUppercaseCheck
            )

            RequirementCheckText(
                text = stringResource(Res.string.register_field_password_check_lowercase),
                valid = state.passwordLowercaseCheck
            )

            RequirementCheckText(
                text = stringResource(Res.string.register_field_password_check_number),
                valid = state.passwordNumberCheck
            )
        }

        PasswordOutlinedTextField(
            value = state.confirmPassword,
            placeholder = {
                Text(stringResource(Res.string.register_field_placeholder_confirm_password))
            },

            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateConfirmPassword(it))
            },
            isError = state.invalidConfirmPassword,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Password,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            colors = transparentTextFieldColors(),
            modifier = Modifier
                .fillMaxWidth()
        )

        // Company details
        Text(
            text = stringResource(Res.string.register_view_company_address_title),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier
                .padding(top = 16.dp)
        )

        HorizontalDivider(
            modifier = Modifier
                .fillMaxWidth()
        )

        CompanyAddressSection(
            state = state,
            onAction = onAction,
            focusManager = focusManager,
        )
    }
}

/**
 * User details section, includes registration fields for user specific details
 *
 * @param state composable state, manages state of all fields
 * @param onAction action handle
 * @param focusManager focus manager
 * @param modifier modifier
 */
@Composable
private fun UserDetailsSection(
    state: UserRegistrationState,
    onAction: (UserRegistrationActions) -> Unit,
    focusManager: FocusManager,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
    ) {
        OutlinedTextField(
            value = state.email,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateEmail(it))
            },
            label = { Text(stringResource(Res.string.register_field_placeholder_email)) },
            colors = transparentTextFieldColors(),
            singleLine = true,
            isError = state.invalidEmail,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier.fillMaxWidth()
        )

        OutlinedTextField(
            value = state.firstName,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateFirstName(it))
            },
            label = { Text(stringResource(Res.string.register_field_placeholder_first_name)) },
            colors = transparentTextFieldColors(),
            singleLine = true,
            isError = state.invalidFirstName,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )

        OutlinedTextField(
            value = state.lastName,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateLastName(it))
            },
            label = { Text(stringResource(Res.string.register_field_placeholder_last_name)) },
            colors = transparentTextFieldColors(),
            singleLine = true,
            isError = state.invalidLastName,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )

        OutlinedTextField(
            value = state.mobileNumber,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateMobileNumber(it))
            },
            label = { Text(stringResource(Res.string.register_field_placeholder_mobile_number)) },
            colors = transparentTextFieldColors(),
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Phone,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )

        OutlinedTextField(
            value = state.companyName,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateCompanyName(it))
            },
            label = { Text(stringResource(Res.string.register_field_placeholder_company_name)) },
            isError = state.invalidCompanyName,
            colors = transparentTextFieldColors(),
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )
    }
}

/**
 * Company addres section, includes registration fields for company specific details
 *
 * @param state composable state, manages state of all fields
 * @param onAction action handle
 * @param focusManager focus manager
 */
@Composable
private fun CompanyAddressSection(
    state: UserRegistrationState,
    onAction: (UserRegistrationActions) -> Unit,
    focusManager: FocusManager,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
    ) {
        OutlinedTextField(
            value = state.companyAddressStreet,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateCompanyAddressStreet(it))
            },
            label = { Text(stringResource(Res.string.register_field_placeholder_company_address_street)) },
            colors = transparentTextFieldColors(),
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )

        OutlinedTextField(
            value = state.companyAddressCity,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateCompanyAddressCity(it))
            },
            label = { Text(stringResource(Res.string.register_field_placeholder_company_address_city)) },
            colors = transparentTextFieldColors(),
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )

        OutlinedTextField(
            value = state.companyAddressPostalCode,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateCompanyAddressPostalCode(it))
            },
            label = {
                Text(
                    stringResource(Res.string.register_field_placeholder_company_address_postal_code)
                )
            },
            colors = transparentTextFieldColors(),
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )

        OutlinedTextField(
            value = state.companyAddressCountry,
            onValueChange = {
                onAction(UserRegistrationActions.OnUpdateCompanyAddressCountry(it))
            },
            label = {
                Text(
                    stringResource(
                        Res.string
                            .register_field_placeholder_company_address_country
                    )
                )
            },
            colors = transparentTextFieldColors(),
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions(
                onNext = { focusManager.moveFocus(FocusDirection.Down) }
            ),
            modifier = Modifier
                .fillMaxWidth()
        )
    }
}

/**
 * Transparent text field colors
 */
@Composable
private fun transparentTextFieldColors() = TextFieldDefaults.colors(
    focusedContainerColor = Color.Transparent,
    unfocusedContainerColor = Color.Transparent,
    disabledContainerColor = Color.Transparent,
    errorContainerColor = Color.Transparent
)

@Preview
@Composable
fun UserRegistrationViewPreview() {
    MaterialTheme {
        UserRegistrationView(
            state = UserRegistrationState(invalidEmail = true),
            onAction = {},
        )
    }
}